import {chain} from 'lodash';
import React, {FC} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../libs/shared/helpers/src/internationalization/react';
import {FrontEvent, HandlerOf} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {DropdownProps} from '../../../../libs/shared/ui/src/components/dropdowns/dropdown';
import {DropdownItem} from '../../../../libs/shared/ui/src/components/dropdowns/dropdownItem';
import {DropdownItemContent} from '../../../../libs/shared/ui/src/components/dropdowns/dropdownItemContent';
import {DropdownList} from '../../../../libs/shared/ui/src/components/dropdowns/dropdownList';
import {SimpleDropdownItem} from '../../../../libs/shared/ui/src/components/dropdowns/simpleDropdownItem';
import {FutureTimeFormatter} from '../../../../srcCommon/formatters/futureTimeFormatter';
import {
  FutureTimeSourcesEnum,
  OnFutureTimeValueSelectOptions,
} from '../../../../srcCommon/models/futureTimeDropdownTypes';
import {futureTimeToDate} from '../../../helpers/modelHelpers';
import {FutureTimeValuesEnum} from '../../../models/api/futureTimeValueModel';
import {ReminderModel} from '../../../models/api/reminderModel';
import {DropdownItemChevron} from '../dropdowns/dropdownItemChevron';
import {DropdownItemSeparator} from '../dropdowns/dropdownItemSeparator';

/*
 * Props.
 */

export interface FutureTimeDropdownSimpleProps extends DropdownProps {
  values: ReadonlyArray<FutureTimeValuesEnum>;
  customSnoozePreset?: ReminderModel;
  onValueSelect: HandlerOf<OnFutureTimeValueSelectOptions>;
  showCustom: HandlerOf<FrontEvent>;
}

/*
 * Style.
 */

const StyledDropdownList = styled(DropdownList)`
  min-width: 250px;
`;

/*
 * Component.
 */

export const FutureTimeDropdownSimple: FC<FutureTimeDropdownSimpleProps> = (props) => (
  <StyledDropdownList {...props}>
    {renderCustomPreset(props)}
    {renderPresets(props)}
    <DropdownItemSeparator />
    <DropdownItem key="custom" onSelect={props.showCustom}>
      <DropdownItemContent>
        <Trans id="custom-futuretime-option">Custom</Trans>
      </DropdownItemContent>
      <DropdownItemChevron />
    </DropdownItem>
  </StyledDropdownList>
);

function renderCustomPreset({customSnoozePreset, onValueSelect}: FutureTimeDropdownSimpleProps) {
  if (!customSnoozePreset || !customSnoozePreset.remindAt) {
    return null;
  }

  const {remindAt} = customSnoozePreset;

  const onPresetSelect = (event: FrontEvent) => {
    event.preventDefault();

    onValueSelect({datetime: remindAt, source: FutureTimeSourcesEnum.SNOOZE_AGAIN});
  };

  return (
    <SimpleDropdownItem key="snooze-dropdown-last-snooze" onSelect={onPresetSelect}>
      <Trans id="reminder-re-snooze">Last snooze</Trans>
    </SimpleDropdownItem>
  );
}

function renderPresets(props: FutureTimeDropdownSimpleProps) {
  const onPresetSelect = (event: FrontEvent, preset: FutureTimeValuesEnum) => {
    event.preventDefault();

    const presetDate = futureTimeToDate(preset);
    props.onValueSelect({datetime: presetDate, source: FutureTimeSourcesEnum.PRESET});
  };

  return chain(props.values)
    .sortBy(futureTimeToDate)
    .map((preset) => (
      <SimpleDropdownItem key={preset} onSelect={(e) => onPresetSelect(e, preset)}>
        <FutureTimeFormatter value={preset} />
      </SimpleDropdownItem>
    ))
    .value();
}
